import { ChevronDown } from 'lucide-react';
import type { NotesGroup } from '@/lib/notes-grouping';
import { cn } from '@/lib/utils';

export interface NotesGroupHeaderProps {
  group: NotesGroup;
}

/**
 * Notes redesign — sticky group header. Direct port of the prototype's
 * `.v1-notes-group-head` styling: glyph + label + count pill +
 * sub-label + a `::after` divider that spans the rest of the row.
 *
 * Sticky behaviour: `position: sticky; top: 0` against the body-
 * scroll container so the header stays visible while items scroll
 * under it. The chevron is decorative for v1 (Q8 — no collapse).
 */
export const NotesGroupHeader = ({ group }: NotesGroupHeaderProps) => (
  <header
    className={cn(
      'sticky top-0 z-[3] mb-2 flex items-center gap-2.5 py-2 pb-2 pt-2.5',
      // Background ensures sticky header doesn't blend into items
      // scrolling under it. The shell paints the same color so it
      // visually disappears from non-sticky context.
      'bg-[var(--shell-bg)]',
      // Trailing divider line via flex-1 sibling (matches the
      // prototype's `::after` pattern but using a real element so
      // Tailwind doesn't have to reach into pseudo-element styling).
      "after:h-px after:flex-1 after:bg-[var(--glass-card-border)] after:opacity-60 after:content-['']",
    )}
  >
    <ChevronDown
      aria-hidden="true"
      className="text-foreground-faint inline-flex size-3.5 transition-transform duration-150"
    />
    <span
      aria-hidden="true"
      className={cn(
        'size-2 flex-shrink-0',
        group.glyphShape === 'square' ? 'rounded-[2.5px]' : 'rounded-full',
      )}
      style={{ background: group.glyphColor }}
    />
    <span
      className={cn(
        'text-foreground whitespace-nowrap font-mono text-[11px] font-semibold uppercase tracking-[0.06em]',
      )}
    >
      {group.label}
    </span>
    <span
      className={cn(
        'text-foreground-muted rounded-full border px-[7px] py-px text-[10.5px] tabular-nums',
        'border-[var(--glass-card-border)] bg-[var(--surface-quiet,var(--secondary))]',
      )}
    >
      {group.items.length}
    </span>
    <span className="text-foreground-faint whitespace-nowrap text-[11px]">
      {group.sub}
    </span>
  </header>
);
